import { type RefObject, useEffect, useState } from "react";
import { Icon } from "@/components/general/icon/Icon";
import { EIcon } from "@/utils/enums";
import styles from "./ScrollToTopButton.module.css";

const POLL_IDLE_TIMEOUT_MS = 300;

type ScrollToTopButtonProps = {
  targetRef: RefObject<HTMLElement | null>;
};

export const ScrollToTopButton = ({ targetRef }: ScrollToTopButtonProps) => {
  const [isScrolledPastTarget, setIsScrolledPastTarget] = useState(false);

  useEffect(() => {
    const node = targetRef.current;
    if (!node) return;

    const evaluate = (top: number) => {
      const next = top < 0;
      setIsScrolledPastTarget((prev) => (prev === next ? prev : next));
    };

    let rafId: number | null = null;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let isIntersecting = false;

    const tick = () => {
      evaluate(node.getBoundingClientRect().top);
      rafId = requestAnimationFrame(tick);
    };

    const stopPolling = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = null;
      if (idleTimer != null) clearTimeout(idleTimer);
      idleTimer = null;
    };

    const onScrollActivity = () => {
      if (!isIntersecting) return;
      if (rafId == null) tick();
      if (idleTimer != null) clearTimeout(idleTimer);
      idleTimer = setTimeout(stopPolling, POLL_IDLE_TIMEOUT_MS);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersecting = entry.isIntersecting;
        if (isIntersecting) {
          evaluate(entry.boundingClientRect.top);
          onScrollActivity();
        } else {
          stopPolling();
          evaluate(entry.boundingClientRect.top);
        }
      },
      { threshold: 0 }
    );
    observer.observe(node);

    window.addEventListener("scroll", onScrollActivity, {
      passive: true,
      capture: true,
    });
    window.addEventListener("wheel", onScrollActivity, { passive: true });
    window.addEventListener("touchmove", onScrollActivity, { passive: true });

    const handleResize = () => evaluate(node.getBoundingClientRect().top);
    window.addEventListener("resize", handleResize);
    evaluate(node.getBoundingClientRect().top);

    return () => {
      observer.disconnect();
      stopPolling();
      window.removeEventListener("scroll", onScrollActivity, { capture: true });
      window.removeEventListener("wheel", onScrollActivity);
      window.removeEventListener("touchmove", onScrollActivity);
      window.removeEventListener("resize", handleResize);
    };
  }, [targetRef]);

  const onClick = () => {
    targetRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (!isScrolledPastTarget) return null;

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.button}
        aria-label="Scroll to method selection"
        onClick={onClick}
      >
        <Icon
          name={EIcon.ScrollToTop}
          width={12}
          height={15}
          alt=""
          aria-hidden="true"
        />
      </button>
    </div>
  );
};
